/**
 * Centered, infinitely looping carousel built on the theme's own SliderComponent.
 *
 * Inherits the native scroll container, drag-to-scroll and momentum settling from
 * SliderComponent, and adds:
 *   - centre-aligned snapping with the active slide highlighted
 *   - an infinite loop, by cloning the full slide set either side and silently
 *     teleporting the scroll position once movement has settled
 */
class TestimonialsSlider extends SliderComponent {
  constructor() {
    super();

    if (!this.slider || !this.nextButton) return;

    this.activeIndex = 0;
    this.originalSlideCount = this.sliderItems.length;
    this.loopEnabled = this.dataset.loop === 'true' && this.originalSlideCount > 2;

    if (this.loopEnabled) {
      // Stops the base class disabling the arrows at either end
      this.enableSliderLooping = true;
      this.buildLoop();
    }

    // The base class only wires drag for <slider-component>, so opt in explicitly
    this.initDragScroll();

    this.slider.addEventListener('scroll', this.onSliderScroll.bind(this));
    this.setActiveSlide();
  }

  buildLoop() {
    const before = document.createDocumentFragment();
    const after = document.createDocumentFragment();

    Array.from(this.sliderItems).forEach((slide, index) => {
      before.appendChild(this.createClone(slide, `before-${index}`));
      after.appendChild(this.createClone(slide, `after-${index}`));
    });

    this.slider.insertBefore(before, this.slider.firstElementChild);
    this.slider.appendChild(after);
    // Clones sit either side, so the track no longer needs end padding to centre
    this.slider.classList.add('is-looping');

    // Re-reads [id^="Slide-"] so the clones are counted, then recalculates offsets
    this.resetPages();

    // Start on the first real slide, in the middle third
    this.jumpToIndex(this.originalSlideCount);
  }

  createClone(slide, suffix) {
    const clone = slide.cloneNode(true);
    clone.id = `${slide.id}-clone-${suffix}`;
    clone.classList.add('testimonials__item--clone');
    clone.setAttribute('aria-hidden', 'true');
    // Never let a duplicate masquerade as a real block in the theme editor
    clone.removeAttribute('data-shopify-editor-block');
    return clone;
  }

  scrollPositionForSlide(index) {
    const slide = this.sliderItemsToShow[index];
    if (!slide) return this.slider.scrollLeft;
    return slide.offsetLeft - (this.slider.clientWidth - slide.clientWidth) / 2;
  }

  getCurrentIndex() {
    if (!this.sliderItemsToShow || !this.sliderItemsToShow.length) return 0;

    const viewportCentre = this.slider.scrollLeft + this.slider.clientWidth / 2;
    let closest = 0;
    let closestDistance = Infinity;

    this.sliderItemsToShow.forEach((slide, index) => {
      const distance = Math.abs(slide.offsetLeft + slide.clientWidth / 2 - viewportCentre);
      if (distance < closestDistance) {
        closestDistance = distance;
        closest = index;
      }
    });

    return closest;
  }

  setActiveSlide() {
    if (!this.sliderItemsToShow || !this.sliderItemsToShow.length) return;

    // Gates the dim/blur styling so nothing is blurred before an active slide exists
    this.slider.classList.add('is-ready');

    this.activeIndex = this.getCurrentIndex();
    this.sliderItemsToShow.forEach((slide, index) => {
      slide.classList.toggle('is-active', index === this.activeIndex);
    });
  }

  onSliderScroll() {
    if (!this.activeFrame) {
      this.activeFrame = requestAnimationFrame(() => {
        this.activeFrame = null;
        this.setActiveSlide();
      });
    }

    // Only rewind once movement has stopped, so the jump is never visible mid-scroll
    clearTimeout(this.settleTimeout);
    this.settleTimeout = setTimeout(() => this.recentreLoop(), 150);
  }

  recentreLoop() {
    if (!this.loopEnabled || this.isPointerDown || this.settleFrame) return;

    const count = this.originalSlideCount;
    const index = this.getCurrentIndex();

    // Already within the real set
    if (index >= count && index < count * 2) return;

    this.jumpToIndex(index < count ? index + count : index - count);
  }

  jumpToIndex(index) {
    const position = this.scrollPositionForSlide(index);

    // slider--no-snap drops scroll-snap and smooth behaviour, so this lands instantly
    this.slider.classList.add('slider--no-snap');
    this.slider.scrollLeft = position;
    void this.slider.offsetWidth;
    this.slider.classList.remove('slider--no-snap');

    this.setActiveSlide();
  }

  // Base version steps by a raw pixel offset; centre alignment needs a real slide target
  onButtonClick(event) {
    event.preventDefault();

    this.cancelSettle();
    this.slider.classList.remove('slider--no-snap');

    const direction = event.currentTarget.name === 'next' ? 1 : -1;
    const index = Math.min(
      Math.max(this.getCurrentIndex() + direction, 0),
      this.sliderItemsToShow.length - 1
    );

    this.slider.scrollTo({ left: this.scrollPositionForSlide(index) });
  }

  // Base version snaps to multiples of the item offset, which assumes start alignment
  settleAfterDrag() {
    const projected = this.slider.scrollLeft - this.dragVelocity * 120;
    const projectedCentre = projected + this.slider.clientWidth / 2;

    let target = null;
    let closestDistance = Infinity;

    this.sliderItemsToShow.forEach((slide) => {
      const centre = slide.offsetLeft + slide.clientWidth / 2;
      const distance = Math.abs(centre - projectedCentre);
      if (distance < closestDistance) {
        closestDistance = distance;
        target = centre - this.slider.clientWidth / 2;
      }
    });

    if (target === null) {
      this.slider.classList.remove('slider--no-snap');
      return;
    }

    const maxScroll = this.slider.scrollWidth - this.slider.clientWidth;
    this.animateScrollTo(Math.min(Math.max(target, 0), maxScroll));
  }

  update() {
    super.update();
    if (!this.loopEnabled) return;

    // super.update() returns early while looping, before it can re-enable the arrows
    this.prevButton.removeAttribute('disabled');
    this.nextButton.removeAttribute('disabled');
  }
}

customElements.define('testimonials-slider', TestimonialsSlider);
